// 몹 풀(고정 용량 SoA). 위치는 매 틱 dt×세계 배율로 적분하고, 정수 타이머는 세계 시계가 1틱 넘어갈 때만 올린다.
// 텔레그래프 슬롯은 멧돼지·엘리트·주술사가 쓴다. 일반 몹의 선딜은 state=Windup + stateTick으로만 표현한다.
import { Rng } from "../core/rng";
import { DEATH_LAUNCH, ELITE_AFFIX_STATS as AFFIX, FIXED_DT, HERO, HIT_STATE, JUGGLE, MOB_ROLE_STATS as ROLE, SHAMAN, KNOCKBACK, MOB_CAPACITY, MOB_FORMATION, MOB_STATS, MOBS, POSTURE, TELEGRAPH_TRIGGER_RATIO as TRIGGER_RATIO, TICK_HZ } from "../shared/config";
import { Ev, TeleSource } from "../shared/events";
import type { LaunchGrant } from "../shared/skills/types";
import { MobKind, MobState, TeleShape } from "../shared/types";
import { CHAMPION_AFFIXES, MobSpecial } from "../shared/specialMonster";
import { PMobSt } from "../shared/partyNet";
import { circleHit, coneHit, lineRectHit, segmentHit, telegraphHit } from "./combat";
import type { GameSim } from "./game";
import { TIMER_MAX } from "./state";
import { ChaseField } from "../shared/chaseField";
import { EliteAffix, MOB_ROLE_BODY, MOB_ROLE_COUNT, MOB_SHOT_CAPACITY, MobRole } from "../shared/mobRoles";
import { MobShots } from "./mobShots";

/** 흐름장을 따르기 시작하는 장 걸음(칸). 이보다 가까우면 진형·직선 추적 그대로(대기 링 3.6m 바깥) */
const CHASE_FAR = 4;
/** 벽에 막힌 뒤 흐름장 우회를 유지하는 틱(60Hz 기준 0.67초) */
const DETOUR_TICKS = 40;
/** 진형 자리가 비어 있다고 보는 반경(m, 이끼 고블린 몸 반경) */
const SLOT_CLEAR = 0.42;

/** 멧돼지 돌진 길이 = 멧돼지 Attack 상태 길이 */
const CHARGE_TICKS = MOB_STATS[MobKind.Boar].attackTicks;
/**
 * 텔레그래프가 있는 공격(멧돼지·엘리트)은 영웅 중심점이 데칼 안일 때만 맞는다.
 * 선딜을 사거리의 이 비율 안에서 시작해야 가만히 선 영웅에게 닿는다(설계 가안).
 */
export const TELEGRAPH_TRIGGER_RATIO = TRIGGER_RATIO;
const WANDER_TICKS = 120;
const WANDER_SPEED = 0.15;
const SHIELD_ARC = (Math.PI * 2) / 3;
const HASH_BUCKETS = 2048;
const HASH_CELL = 2;
/** 처치 세기별 발사 속도(KillPower 순서: 일반, 마무리타·스킬, 각성) */
const LAUNCH = [DEATH_LAUNCH.basic, DEATH_LAUNCH.heavy, DEATH_LAUNCH.awaken] as const;

export class MobController {
  readonly waveId = new Int16Array(MOB_CAPACITY).fill(-1);
  /** 싸울 수 있는 몹 수(Dying 제외). MobsView.aliveCount는 Dying까지 센다 */
  living = 0;
  private readonly cooldown = new Uint16Array(MOB_CAPACITY);
  private readonly telegraph = new Int16Array(MOB_CAPACITY).fill(-1);
  private readonly knockX = new Float32Array(MOB_CAPACITY);
  private readonly knockZ = new Float32Array(MOB_CAPACITY);
  private readonly charged = new Uint8Array(MOB_CAPACITY);
  /** 처치 뒤 튕겨 날아가는 속도(m/s) */
  private readonly flyX = new Float32Array(MOB_CAPACITY);
  private readonly flyZ = new Float32Array(MOB_CAPACITY);
  private readonly flyY = new Float32Array(MOB_CAPACITY);
  /** 공중 상태(Airborne) 궤적: 체공 틱·시작 높이·포물선 높이. 재띄우기마다 갱신(COMBAT-FEEL 2.2) */
  private readonly airDur = new Uint16Array(MOB_CAPACITY);
  private readonly airBase = new Float32Array(MOB_CAPACITY);
  private readonly airLift = new Float32Array(MOB_CAPACITY);
  /** 이번 공중 체류에서 재띄운 횟수 */
  private readonly juggles = new Uint8Array(MOB_CAPACITY);
  /** 이번 체공에서 지난 구간들에 쓴 누적 틱(현재 구간은 stateTick). 합이 HIT_STATE.airCapTicks를 넘으면 강제 다운 */
  private readonly airTotal = new Uint16Array(MOB_CAPACITY);
  /** 체공 상한을 넘겨 강제 낙하 중인지(0/1). 공중에서는 다운에 들어가지 않고 forcedFallSpeed로 떨어져 y=0에서 다운한다. setState가 지운다 */
  private readonly forcedFall = new Uint8Array(MOB_CAPACITY);
  /** 엘리트의 띄움·다운 저항 판정 전용 결정적 스트림(시드는 판의 시드에서 파생, 다른 난수 소비를 흔들지 않는다) */
  private readonly resistRng: Rng;
  /** 멧돼지 돌진 경고선(선딜 시작 위치·방향). 돌진이 벽에 미끄러져도 그려진 선 밖은 안전하다 */
  private readonly chargeX = new Float32Array(MOB_CAPACITY);
  private readonly chargeZ = new Float32Array(MOB_CAPACITY);
  private readonly chargeLength = new Float32Array(MOB_CAPACITY);
  private readonly born = new Float64Array(MOB_CAPACITY);
  private readonly chargeAngle = new Float32Array(MOB_CAPACITY);
  private readonly heads = new Int16Array(HASH_BUCKETS);
  private readonly next = new Int16Array(MOB_CAPACITY);
  private readonly cellX = new Int16Array(MOB_CAPACITY);
  private readonly cellZ = new Int16Array(MOB_CAPACITY);
  /** 진형: 몹별 공격 자리(-1 = 대기), 자리별 주인(-1 = 빔) */
  readonly slotOf = new Int8Array(MOB_CAPACITY).fill(-1);
  private readonly slotOwner = new Int16Array(MOB_FORMATION.slots).fill(-1);
  private formationTick = 0;
  /**
   * 파티 판 꼭두각시(레인 D, game/partyLink.ts): 1이면 서버 몹을 따라 그리기만 한다. 위치·방향·공격 준비는 서버 값(PartyLink.tx·tz·tf·sst),
   * AI 이동·로컬 공격 판정·진형·서로 밀기를 건너뛴다. 피격 반응(경직·띄움 자세)은 보이되 다운에 들어가지 않는다
   */
  readonly puppet = new Uint8Array(MOB_CAPACITY);
  /** 대기 몹 어그로 반경(m). 던전 = MOBS.aggroRadius, 필드는 무리를 골라 당기도록 좁힌다(레인 F, reset이 되돌린다) */
  aggroRadius: number = MOBS.aggroRadius;
  /** 추적 흐름장(영웅 칸 기준, 모든 몹 공유). 지형 뒤에서 멈추지 않게 돌아온다(C11) */
  readonly field = new ChaseField();
  /** 흐름장 사용 여부(계측 도구가 전후 비교에만 끈다. 게임은 늘 켠다) */
  fieldOn = true;
  /** 직전에 벽에 막힌 몹이 흐름장 우회를 유지할 남은 틱 */
  private readonly detour = new Uint8Array(MOB_CAPACITY);
  private readonly aim = { x: 0, z: 0 };
  /** C2 투척형 투사체 풀(GameView.mobShots) */
  readonly shots = new MobShots();
  /** C2 졸개(소환·분열) 표지: 1이면 처치 EXP·드롭·목표 집계가 없다. 주인 번호·세대로 소환자당 상한을 센다 */
  private readonly minion = new Uint8Array(MOB_CAPACITY);
  private readonly minionOwner = new Int16Array(MOB_CAPACITY).fill(-1);
  private readonly minionOwnerGen = new Uint16Array(MOB_CAPACITY);
  /** 소환형이 지금까지 마친 소환 수 */
  private readonly casts = new Uint8Array(MOB_CAPACITY);
  /** 보호막 정예의 남은·최대 보호막(HP 단위) */
  private readonly shieldHp = new Float32Array(MOB_CAPACITY);
  private readonly shieldMax = new Float32Array(MOB_CAPACITY);
  /** 1이면 돌진 중(Attack 상태 종료·중단 때 MobDash 끝 이벤트를 낸다) */
  private readonly dashing = new Uint8Array(MOB_CAPACITY);
  constructor(private readonly g: GameSim) { this.resistRng = new Rng((g.rng.state ^ 0x9e3779b9) >>> 0); }

  /** 풀 비우기. 세대(gen)는 유지해 다음 스폰에서 계속 증가한다 */
  reset(): void {
    const m = this.g.mobs;
    for (let i = 0; i < m.capacity; i++) this.g.releaseTelegraph(this.telegraph[i]);
    m.state.fill(MobState.Inactive); m.aliveCount = 0; this.living = 0;
    m.hitTick.fill(0xffff); m.y.fill(0); m.prevY.fill(0); m.stateTick.fill(0);
    m.posture.fill(0); m.guardBroken.fill(0); m.rooted.fill(0); m.special.fill(0); m.affix.fill(0);
    this.waveId.fill(-1); this.cooldown.fill(0); this.telegraph.fill(-1);
    this.knockX.fill(0); this.knockZ.fill(0); this.charged.fill(0); this.forcedFall.fill(0);
    this.flyX.fill(0); this.flyZ.fill(0); this.flyY.fill(0);
    this.airDur.fill(0); this.airBase.fill(0); this.airLift.fill(0); this.juggles.fill(0); this.airTotal.fill(0);
    this.slotOf.fill(-1); this.slotOwner.fill(-1); this.formationTick = 0;
    this.aggroRadius = MOBS.aggroRadius;
    this.puppet.fill(0);
    // C2: 날아가던 투사체의 경고 슬롯은 같은 모양일 때만 거둔다(다른 경고에 재사용됐으면 건드리지 않는다)
    for (let s = 0; s < MOB_SHOT_CAPACITY; s++) if (this.shots.alive[s] !== 0 && this.shotSlotValid(s)) this.g.releaseTelegraph(this.shots.tele[s]);
    this.shots.alive.fill(0); this.shots.progress.fill(0); this.shots.tele.fill(-1); this.shots.owner.fill(-1);
    m.role.fill(0); m.eliteAffix.fill(0); m.shieldFrac.fill(0);
    this.minion.fill(0); this.minionOwner.fill(-1); this.casts.fill(0); this.shieldHp.fill(0); this.shieldMax.fill(0); this.dashing.fill(0);
  }

  radius(i: number): number { return MOB_STATS[this.g.mobs.kind[i]].radius; }

  /** 진형을 따르는 근접 몹(원거리 돌진·주술사는 제외) */
  private melee(i: number): boolean {
    const m = this.g.mobs;
    return m.kind[i] !== MobKind.Boar && m.kind[i] !== MobKind.Shaman && m.role[i] !== MobRole.Thrower && m.role[i] !== MobRole.Rusher;
  }
  /** 돌진으로 때리는 몹(멧돼지·돌진형) */
  private dasher(i: number): boolean { const m = this.g.mobs; return m.kind[i] === MobKind.Boar || m.role[i] === MobRole.Rusher; }
  /** Attack 상태 길이(틱). 돌진형은 돌진 시간, 투척형은 던진 뒤 자세 */
  private attackTicksOf(i: number): number {
    const r = this.g.mobs.role[i];
    return r === MobRole.Rusher ? ROLE.rusher.dashTicks : r === MobRole.Thrower ? ROLE.thrower.attackTicks : MOB_STATS[this.g.mobs.kind[i]].attackTicks;
  }
  /** 직군·접두를 반영하기 전 선딜 길이(틱) */
  private baseWindup(i: number): number {
    const m = this.g.mobs;
    return m.role[i] === MobRole.Thrower ? ROLE.thrower.windup : m.role[i] === MobRole.Rusher ? ROLE.rusher.windup : m.role[i] === MobRole.Summoner ? ROLE.summoner.windup : MOB_STATS[m.kind[i]].windup;
  }
  private swift(i: number): boolean { const m = this.g.mobs; return m.elite[i] !== 0 && m.eliteAffix[i] === EliteAffix.Swift; }
  /** 졸개(소환·분열)인지. 처치 보상·목표 집계에서 빠진다 */
  isMinion(i: number): boolean { return this.minion[i] !== 0; }

  /** 공격 자리를 놓는다(처치·공중·비활성) */
  private releaseSlot(i: number): void {
    const s = this.slotOf[i];
    if (s >= 0) { if (this.slotOwner[s] === i) this.slotOwner[s] = -1; this.slotOf[i] = -1; }
  }

  /**
   * 빈 공격 자리를 가장 가까운 대기 근접 몹에게 준다. 자리는 영웅 둘레 고정 각도라 영웅이 움직여도 몹이 같은 쪽을 맡는다.
   * 자리를 맡을 수 있는 몹: 추적 중(Chase)·선딜·공격·경직 상태의 근접 몹
   */
  private assignSlots(): void {
    const g = this.g, m = g.mobs, h = g.hero, F = MOB_FORMATION;
    for (let s = 0; s < F.slots; s++) {
      const o = this.slotOwner[s];
      if (o >= 0) {
        const st = m.state[o];
        if (this.slotOf[o] !== s || st === MobState.Inactive || st === MobState.Dying || st === MobState.Airborne || st === MobState.Down || st === MobState.Wakeup || st === MobState.Idle) this.releaseSlot(o), this.slotOwner[s] = -1;
      }
    }
    for (let s = 0; s < F.slots; s++) {
      if (this.slotOwner[s] >= 0) continue;
      const a = (s / F.slots) * Math.PI * 2;
      const sx = h.x + Math.sin(a) * F.slotRadius, sz = h.z + Math.cos(a) * F.slotRadius;
      // 벽·구멍·용암 속 자리는 맡기지 않는다(닿을 수 없는 자리를 겨누다 영웅 앞 3~4m에서 서는 정지, C11 계측)
      if (!g.walkable(sx, sz, SLOT_CLEAR, -1, true)) continue;
      let best = -1, bestD = Infinity;
      for (let i = 0; i < m.capacity; i++) {
        if (this.slotOf[i] >= 0 || !this.melee(i) || m.state[i] !== MobState.Chase) continue;
        const d = Math.hypot(m.x[i] - sx, m.z[i] - sz);
        if (d < bestD) { bestD = d; best = i; }
      }
      if (best >= 0) { this.slotOwner[s] = best; this.slotOf[best] = s; }
    }
    let held = 0;
    for (let s = 0; s < F.slots; s++) if (this.slotOwner[s] >= 0) held++;
    this.slotsHeld = held;
  }
  /** 지금 진형 자리를 맡은 몹 수. 0이면(영웅이 좁은 곳에 서서 빈 자리가 없을 때) 자리 없는 몹도 대기 링에 서지 않고 붙는다 */
  private slotsHeld = 0;
  /** 텔레그래프 슬롯을 쓰는 몹(멧돼지·엘리트) */
  telegraphed(i: number): boolean { return this.g.mobs.kind[i] === MobKind.Boar || this.g.mobs.elite[i] !== 0; }

  /**
   * 피격 경직·넉백을 받지 않는 상태: 슈퍼아머(멧돼지·엘리트) 또는 등장 연출 중.
   * 이미 공중·다운·기상 상태인 몹은 슈퍼아머가 아니다(재띄움은 받고, 다운·기상은 knockback이 상태를 바꾸지 않는다).
   */
  armored(i: number): boolean {
    const st = this.g.mobs.state[i];
    if (st === MobState.Airborne || st === MobState.Down || st === MobState.Wakeup) return false;
    return this.g.mobs.guardBroken[i] === 0 && (this.telegraphed(i) || st === MobState.Spawning);
  }

  /** 기상 무적 중(COMBAT-FEEL 2.7): 모든 피해를 무시한다 */
  invulnerable(i: number): boolean { return this.g.mobs.state[i] === MobState.Wakeup; }

  /** 다운 중(추격 피해 60%) */
  isDown(i: number): boolean { return this.g.mobs.state[i] === MobState.Down; }

  /**
   * 슈퍼아머 엘리트가 마무리타·낙화 같은 띄우는 타격을 받아 띄움 판정(저항 굴림)에 들어갈 수 있는지.
   * 멧돼지는 슈퍼아머 면역이라 대상이 아니고, 자세가 깨져 무방비일 때는 armored가 풀려 일반 몹과 같은 경로를 탄다.
   */
  eliteLaunchable(i: number): boolean {
    const m = this.g.mobs, st = m.state[i];
    return m.elite[i] !== 0 && m.guardBroken[i] === 0
      && st !== MobState.Spawning && st !== MobState.Dying && st !== MobState.Inactive && st !== MobState.Down && st !== MobState.Wakeup;
  }

  /** 선딜 후반(지역 공세 commitAfter 이후, 지역1·2 = MOBS.commitAfter): 가벼운 기본공격으로는 공격을 끊을 수 없다 */
  committed(i: number): boolean {
    const m = this.g.mobs;
    return m.kind[i] !== MobKind.Shaman && m.state[i] === MobState.Windup && m.stateTick[i] >= this.windupTicks(i) * this.g.mobTempo.commitAfter;
  }

  /**
   * 선딜 길이(틱). 경고 데칼이 있는 공격(멧돼지·엘리트·주술사)은 데칼 길이 = 반응 시간이라 원래 값 그대로,
   * 데칼 없는 일반 근접 몹만 지역 공세 windupMult를 곱한다(c018: 지역3·4 몹이 더 빨리 휘두른다)
   */
  windupTicks(i: number): number {
    const m = this.g.mobs, base = this.baseWindup(i);
    // 신속 정예: 선딜 ×0.8(경고 데칼 길이도 같이 줄어 반응 시간이 그만큼 짧아진다)
    const w = this.swift(i) ? Math.round(base * AFFIX.swift.windupMult) : base;
    return m.role[i] !== MobRole.Base || this.telegraph[i] >= 0 || this.telegraphed(i) || m.kind[i] === MobKind.Shaman ? w : Math.round(w * this.g.mobTempo.windupMult);
  }

  /**
   * 몹 한 마리를 내보낸다. role = C2 직군(MOB_ROLE_BODY와 몸이 안 맞으면 Base), eliteAffix = 정예 접두(elite일 때만 뜻이 있다, 기본 0).
   * 스폰 때 접두가 있으면 MobAffix(c=0)를 낸다
   */
  spawn(kind: number, elite: boolean, x: number, z: number, wave = -1, role = 0, eliteAffix = 0): number {
    const g = this.g, m = g.mobs;
    if (kind < 0 || kind >= MOB_STATS.length || !g.walkable(x, z, MOB_STATS[kind].radius, -1, true)) return -1;
    let slot = -1;
    for (let i = 0; i < m.capacity; i++) if (m.state[i] === MobState.Inactive) { slot = i; break; }
    if (slot < 0) return -1;
    elite = elite && kind !== MobKind.Shaman;
    if (role < 0 || role >= MOB_ROLE_COUNT || MOB_ROLE_BODY[role] !== kind) role = MobRole.Base;
    if (!elite || eliteAffix < 0 || eliteAffix >= 4) eliteAffix = EliteAffix.None;
    this.born[slot] = g.threats.now;
    m.gen[slot] = (m.gen[slot] + 1) & 0xffff;
    m.state[slot] = MobState.Spawning; m.stateTick[slot] = 0; m.kind[slot] = kind; m.elite[slot] = elite ? 1 : 0;
    g.snapMob(slot, x, z); m.y[slot] = m.prevY[slot] = 0;
    // 정수 체력(2026-10-20 수치 재설계: 104×1.2 같은 소수가 결과 기록·저장 검증을 깨뜨렸다)
    const roleHp = role === MobRole.Thrower ? ROLE.thrower.hpMult : role === MobRole.Rusher ? ROLE.rusher.hpMult : 1;
    m.hp[slot] = m.maxHp[slot] = Math.round(MOB_STATS[kind].hp * g.mobHpMult * (elite ? MOBS.elite.hpMult : 1) * roleHp);
    m.role[slot] = role; m.eliteAffix[slot] = eliteAffix; m.shieldFrac[slot] = 0;
    this.minion[slot] = 0; this.minionOwner[slot] = -1; this.casts[slot] = 0; this.dashing[slot] = 0;
    this.shieldHp[slot] = this.shieldMax[slot] = 0;
    if (eliteAffix === EliteAffix.Bulwark) { this.shieldHp[slot] = this.shieldMax[slot] = Math.max(1, Math.round(m.maxHp[slot] * AFFIX.bulwark.shieldFrac)); m.shieldFrac[slot] = 1; }
    m.posture[slot] = 0; m.guardBroken[slot] = 0; m.rooted[slot] = 0; m.special[slot] = 0; m.affix[slot] = 0;
    m.facing[slot] = g.rng.range(-Math.PI, Math.PI); m.hitTick[slot] = 0xffff;
    this.waveId[slot] = wave; this.cooldown[slot] = 0; this.telegraph[slot] = -1; this.charged[slot] = 0;
    this.knockX[slot] = 0; this.knockZ[slot] = 0; this.flyX[slot] = 0; this.flyZ[slot] = 0; this.flyY[slot] = 0;
    this.detour[slot] = 0;
    // 웨이브 몹은 처음부터 영웅을 바라보고 나온다
    if (wave >= 0) m.facing[slot] = Math.atan2(g.hero.x - x, g.hero.z - z);
    // 스페셜 몬스터 훅(챔피언 승격 판정): 계획이 없는 판에서는 바로 돌아온다
    if (g.special.active && !this.spawningMinion) g.special.onSpawn(slot, kind, elite, wave);
    m.aliveCount++; this.living++;
    const e = g.emit(Ev.MobSpawn); e.x = x; e.z = z; e.a = slot;
    if (eliteAffix !== EliteAffix.None) { const a = g.emit(Ev.MobAffix); a.x = x; a.z = z; a.a = slot; a.b = eliteAffix; a.c = 0; }
    return slot;
  }

  /** 졸개를 내보내는 중(스페셜 몬스터 승격 후보에서 뺀다) */
  private spawningMinion = false;
  /** 졸개 고블린 한 마리를 (x,z) 근처 빈 자리에 낸다. 못 내면 -1 */
  private spawnMinion(x: number, z: number, wave: number, owner: number, hpMult: number): number {
    this.spawningMinion = true;
    const j = this.g.flowController.spawnNear(MobKind.Goblin, false, x, z, wave);
    this.spawningMinion = false;
    if (j >= 0) this.markMinion(j, owner, hpMult);
    return j;
  }

  /** 졸개로 표지(처치 보상·목표 집계 없음). owner >= 0이면 소환자당 상한 집계에 든다. hp는 몹 체력 배율을 곱한 값에 hpMult를 더 곱한다 */
  private markMinion(j: number, owner: number, hpMult: number): void {
    const m = this.g.mobs;
    this.minion[j] = 1; this.minionOwner[j] = owner; this.minionOwnerGen[j] = owner >= 0 ? m.gen[owner] : 0;
    m.hp[j] = m.maxHp[j] = Math.max(1, Math.round(m.maxHp[j] * hpMult));
  }

  /** 살아 있는(처치 전) 졸개 수. owner >= 0이면 그 소환자의 졸개만 센다 */
  minionsAlive(owner = -1): number {
    const m = this.g.mobs;
    let n = 0;
    for (let j = 0; j < m.capacity; j++) {
      if (this.minion[j] === 0 || m.state[j] === MobState.Inactive || m.state[j] === MobState.Dying) continue;
      if (owner >= 0 && (this.minionOwner[j] !== owner || this.minionOwnerGen[j] !== m.gen[owner])) continue;
      n++;
    }
    return n;
  }

  /**
   * 보호막 흡수. 보호막이 있으면 피해를 먼저 깎고 남은 피해(체력에 들어갈 몫)를 돌려준다. 막이 깨지는 순간 MobAffix(c=1).
   * 꼭두각시(파티 판 서버 권위)에는 막이 없다
   */
  absorb(i: number, damage: number): number {
    const sh = this.shieldHp[i];
    if (sh <= 0 || damage <= 0 || this.puppet[i] !== 0) return damage;
    const take = Math.min(sh, damage), left = sh - take, m = this.g.mobs;
    this.shieldHp[i] = left; m.shieldFrac[i] = left > 0 ? left / this.shieldMax[i] : 0;
    if (left <= 0) { const e = this.g.emit(Ev.MobAffix); e.x = m.x[i]; e.z = m.z[i]; e.a = i; e.b = EliteAffix.Bulwark; e.c = 1; }
    return damage - take;
  }

  /**
   * 처치: Dying으로 전환(슬롯은 dyingTicks 뒤 비운다). (dirX, dirZ) 방향으로 power 세기만큼 튕겨 날아간다.
   * 방향이 0이면 제자리에서 쓰러진다(QA·테스트 경로).
   */
  /**
   * 속박(c022 그물 덫): ticks 동안 추적·배회 이동을 멈춘다. 엘리트는 HIT_STATE.eliteResist 확률로 저항(0 반환), 등장·사망·비활성은 0.
   * 더 긴 속박만 덮어쓴다. 실제로 건 틱을 돌려준다
   */
  root(i: number, ticks: number): number {
    const m = this.g.mobs, st = m.state[i];
    if (st === MobState.Inactive || st === MobState.Dying || st === MobState.Spawning) return 0;
    if (m.elite[i] !== 0 && this.resistRng.next() < HIT_STATE.eliteResist) return 0;
    const t = Math.min(0xfffe, Math.max(0, Math.round(ticks)));
    if (t > m.rooted[i]) m.rooted[i] = t;
    return t;
  }

  /**
   * 보상 없이 사라진다(황금 고블린이 12초 안에 잡히지 않아 땅속으로 들어감). 처치가 아니므로 Kill 이벤트·처치 수·드롭이 없다.
   * Dying 상태로 두되 hp가 남아 있다(렌더가 hp > 0인 Dying을 '가라앉으며 사라짐'으로 그린다). 슬롯은 dyingTicks 뒤 비운다
   */
  vanish(i: number): void {
    const m = this.g.mobs;
    if (m.state[i] === MobState.Inactive || m.state[i] === MobState.Dying) return;
    this.setState(i, MobState.Dying);
    this.releaseSlot(i);
    this.living--; this.knockX[i] = 0; this.knockZ[i] = 0;
    this.flyX[i] = 0; this.flyZ[i] = 0; this.flyY[i] = 0;
  }

  kill(i: number, dirX = 0, dirZ = 0, power = 0): void {
    const m = this.g.mobs;
    if (m.state[i] === MobState.Inactive || m.state[i] === MobState.Dying) return;
    this.setState(i, MobState.Dying);
    this.releaseSlot(i);
    this.living--; m.hp[i] = 0; this.knockX[i] = 0; this.knockZ[i] = 0;
    const l = LAUNCH[Math.max(0, Math.min(2, power))];
    // 같은 동작에 죽은 몹들이 똑같이 날지 않게 세기를 조금 흔든다
    const jitter = 0.85 + this.g.rng.next() * 0.3;
    this.flyX[i] = dirX * l.speed * jitter; this.flyZ[i] = dirZ * l.speed * jitter;
    this.flyY[i] = dirX === 0 && dirZ === 0 ? 0 : l.up * jitter;
    // 분열 정예: 처치 때 작은 고블린 2마리(졸개라 다시 분열하지 않는다). 꼭두각시(서버 권위)는 나누지 않는다
    if (m.elite[i] !== 0 && m.eliteAffix[i] === EliteAffix.Splitting && this.puppet[i] === 0 && !this.g.party.active) this.split(i);
  }

  private split(i: number): void {
    const g = this.g, m = g.mobs, S = AFFIX.splitting;
    const ev = g.emit(Ev.MobAffix); ev.x = m.x[i]; ev.z = m.z[i]; ev.a = i; ev.b = EliteAffix.Splitting; ev.c = 2;
    for (let n = 0; n < S.count; n++) {
      const a = (n / S.count) * Math.PI * 2 + m.facing[i];
      this.spawnMinion(m.x[i] + Math.sin(a) * S.ring, m.z[i] + Math.cos(a) * S.ring, this.waveId[i], -1, S.hpMult);
    }
  }

  /** 처치된 몹의 비행: 중력·한 번 튀기·땅 마찰. 벽은 moveMob이 막는다 */
  private fly(i: number, dt: number, radius: number): void {
    const m = this.g.mobs;
    if (this.flyX[i] !== 0 || this.flyZ[i] !== 0) this.g.moveMob(i, this.flyX[i] * dt, this.flyZ[i] * dt, radius);
    let vy = this.flyY[i] - DEATH_LAUNCH.gravity * dt, y = m.y[i] + vy * dt;
    if (y <= 0) {
      y = 0;
      vy = vy < -2 ? -vy * DEATH_LAUNCH.bounce : 0;
      const drag = Math.max(0, 1 - DEATH_LAUNCH.groundDrag * dt);
      this.flyX[i] *= drag; this.flyZ[i] *= drag;
    }
    m.y[i] = y; this.flyY[i] = vy;
  }

  /** 이번 공중 체류에서 재띄운 횟수(0..JUGGLE.maxRelaunch) */
  juggleCount(i: number): number { return this.juggles[i]; }

  /**
   * 공중에 뜬 몹을 현재 높이에서 다시 띄운다(COMBAT-FEEL 2.2). 상한을 넘으면 아무것도 바꾸지 않아 지금 궤적을 끝까지 그린다.
   * 높이는 0.75^n배, 체공은 √높이 비례, 시작 높이는 맞은 순간의 y라 땅으로 끊기지 않는다.
   */
  private relaunch(i: number, dirX: number, dirZ: number): void {
    const m = this.g.mobs;
    if (this.juggles[i] >= JUGGLE.maxRelaunch || this.forcedFall[i] !== 0) return; // 강제 낙하 중에는 더 띄우지 않는다
    const n = ++this.juggles[i], ratio = JUGGLE.liftDecay ** n;
    const duration = Math.max(1, Math.round(KNOCKBACK.finisherStun * Math.sqrt(ratio)));
    const y = m.y[i];
    // 지난 구간에서 이미 난 시간을 누적한다(체공 3초 상한)
    this.airTotal[i] += m.stateTick[i];
    this.setState(i, MobState.Airborne);
    this.airBase[i] = y; this.airLift[i] = KNOCKBACK.finisherLift * ratio; this.airDur[i] = duration;
    this.knockX[i] = (dirX * JUGGLE.pushDist) / duration;
    this.knockZ[i] = (dirZ * JUGGLE.pushDist) / duration;
    m.y[i] = y;
    const e = this.g.emit(Ev.Juggle);
    e.x = m.x[i]; e.y = y; e.z = m.z[i]; e.a = n; e.b = i; e.c = duration;
  }

  /**
   * 맞은 몹의 상태를 바꾼다. 다운·기상 중에는 추가 타격이 상태를 바꾸지 않는다(다운 추격은 피해만, 기상은 무적).
   * 엘리트는 마무리타 계열 띄움 시도마다 HIT_STATE.eliteResist 확률로 저항한다(저항하면 아무것도 바꾸지 않음).
   */
  knockback(i: number, dirX: number, dirZ: number, finisher: boolean): void {
    const state = this.g.mobs.state[i];
    if (state === MobState.Down || state === MobState.Wakeup) return;
    // 엘리트는 공중 재띄움도 띄움 시도마다 저항 굴림을 한다(COMBAT-FEEL 2.7, 저항하면 궤적을 되감지 않는다)
    if (state === MobState.Airborne) { if (!this.eliteResists(i)) this.relaunch(i, dirX, dirZ); return; }
    if (finisher && this.g.mobs.elite[i] !== 0 && this.g.mobs.guardBroken[i] === 0 && this.resistRng.next() < HIT_STATE.eliteResist) return;
    const duration = finisher ? KNOCKBACK.finisherStun : KNOCKBACK.normalStun;
    const dist = finisher ? KNOCKBACK.finisherDist : KNOCKBACK.normalDist;
    this.setState(i, finisher ? MobState.Airborne : MobState.Stunned);
    this.knockX[i] = (dirX * dist) / duration;
    this.knockZ[i] = (dirZ * dist) / duration;
    this.g.mobs.y[i] = 0;
    if (finisher) { this.airDur[i] = duration; this.airBase[i] = 0; this.airLift[i] = KNOCKBACK.finisherLift; this.juggles[i] = 0; this.airTotal[i] = 0; }
  }

  /**
   * 런처(스킬 타임라인 grants.launch, COMBAT-FEEL 2.11)가 이 몹을 띄울 수 있는지. 저항 굴림 전의 규칙 판정이다.
   * 이미 공중이면 재띄움 대상이고, 다운·기상·등장·사망 중이면 아니다. 슈퍼아머(멧돼지·무방비 아닌 엘리트) 중 엘리트만 저항 굴림 후보다.
   */
  /** 엘리트(무방비 아님)가 이번 띄움 시도를 저항하는지 굴린다. 엘리트가 아니면 굴리지 않는다(난수 소비 없음) */
  private eliteResists(i: number): boolean {
    const m = this.g.mobs;
    return m.elite[i] !== 0 && m.guardBroken[i] === 0 && this.resistRng.next() < HIT_STATE.eliteResist;
  }

  launchable(i: number): boolean {
    const st = this.g.mobs.state[i];
    if (st === MobState.Down || st === MobState.Wakeup || st === MobState.Spawning || st === MobState.Dying || st === MobState.Inactive) return false;
    if (st === MobState.Airborne) return true;
    return !this.armored(i) || this.eliteLaunchable(i);
  }

  /**
   * 런처 띄움: 맞은 몹을 grant.lift(m) 높이로 grant.airTicks 동안 띄우고 영웅 쪽으로 grant.pull(m) 끌어올린다
   * (몸 가장자리 + 0.2m 이내로는 끌지 않는다. 끌어올려야 이어지는 공중 추가타가 사거리 안에 든다).
   * 이미 뜬 몹은 재띄움 규칙(2.2절, 최대 3회), 엘리트는 띄움 시도마다 HIT_STATE.eliteResist 확률로 저항한다. 객체를 만들지 않는다.
   * 반환값: 띄우기(재띄움 시도 포함)가 저항·불가 규칙에 막히지 않았으면 true. combat이 Hit 이벤트의 Launch 플래그를 정하는 데 쓴다.
   */
  launch(i: number, dirX: number, dirZ: number, grant: LaunchGrant): boolean {
    const g = this.g, m = g.mobs;
    if (!this.launchable(i)) return false;
    if (m.state[i] === MobState.Airborne) { if (this.eliteResists(i)) return false; this.relaunch(i, dirX, dirZ); return true; }
    if (this.eliteResists(i)) return false;
    const h = g.hero, dx = h.x - m.x[i], dz = h.z - m.z[i], d = Math.hypot(dx, dz);
    const move = d > 1e-6 ? Math.min(grant.pull, Math.max(0, d - HERO.radius - this.radius(i) - 0.2)) : 0;
    this.setState(i, MobState.Airborne);
    this.airDur[i] = grant.airTicks; this.airBase[i] = 0; this.airLift[i] = grant.lift; this.juggles[i] = 0; this.airTotal[i] = 0;
    this.knockX[i] = move > 0 ? (dx / d) * move / grant.airTicks : 0;
    this.knockZ[i] = move > 0 ? (dz / d) * move / grant.airTicks : 0;
    m.y[i] = 0;
    return true;
  }

  /**
   * 체공 연장 훅(공중 고정·부양 계열 스킬용). 현재 높이에서 남은 시간 + ticks에 걸쳐 천천히 내려오도록 궤적을 다시 잡는다.
   * 누적 체공이 HIT_STATE.airCapTicks를 넘으면 step이 강제 낙하시키고 착지하면 다운시킨다(무한 콤보 방지). 공중이 아니거나 강제 낙하 중이면 무시.
   */
  extendAir(i: number, ticks: number): void {
    const m = this.g.mobs;
    if (m.state[i] !== MobState.Airborne || ticks <= 0 || this.forcedFall[i] !== 0) return;
    const remaining = Math.max(0, this.airDur[i] - m.stateTick[i]);
    this.airTotal[i] += m.stateTick[i];
    m.stateTick[i] = 0;
    this.airBase[i] = m.y[i]; this.airLift[i] = 0;
    this.airDur[i] = Math.min(0xffff, remaining + ticks);
  }

  /** 누적 체공 틱(지난 구간 + 현재 구간). 테스트·QA 관측용 */
  airTicks(i: number): number { return this.g.mobs.state[i] === MobState.Airborne ? this.airTotal[i] + this.g.mobs.stateTick[i] : 0; }

  /** 다운 진입. forced = 체공 상한으로 인한 강제 다운(공중에서 빠르게 낙하한 뒤 착지 틱에 들어온다) */
  private enterDown(i: number, forced: boolean): void {
    const g = this.g, m = g.mobs;
    this.setState(i, MobState.Down);
    this.knockX[i] = 0; this.knockZ[i] = 0; this.airTotal[i] = 0;
    if (!forced) m.y[i] = 0;
    const e = g.emit(Ev.MobDown); e.x = m.x[i]; e.y = m.y[i]; e.z = m.z[i]; e.a = i; e.b = forced ? 1 : 0; e.c = HIT_STATE.downTicks;
  }

  /** 기상 진입: 무적(HIT_STATE.wakeTicks) */
  private enterWake(i: number): void {
    const g = this.g, m = g.mobs;
    this.setState(i, MobState.Wakeup);
    m.y[i] = 0;
    const e = g.emit(Ev.MobWake); e.x = m.x[i]; e.y = 0; e.z = m.z[i]; e.a = i; e.b = HIT_STATE.wakeTicks;
  }

  /** 무방비(자세가 깨진 뒤 POSTURE.breakTicks 동안)인지. 처형 대상 판정에 쓴다 */
  vulnerable(i: number): boolean { return this.g.mobs.guardBroken[i] > 0; }

  /** 자세를 올리고 가득 차면 가드 브레이크를 건다. 반환값은 이번 타격으로 깨졌는지다. */
  addPosture(i: number, amount: number, dirX: number, dirZ: number): boolean {
    const m = this.g.mobs;
    // 일반 몹도 작은 막대를 갖는다(COMBAT-FEEL 2.4). 등장 연출·처치 중에는 쌓이지 않는다
    const st = m.state[i];
    if (m.guardBroken[i] > 0 || amount <= 0 || st === MobState.Spawning || st === MobState.Dying || st === MobState.Inactive || st === MobState.Down || st === MobState.Wakeup) return false;
    m.posture[i] = Math.min(1, m.posture[i] + amount);
    if (m.posture[i] < 1) return false;
    m.posture[i] = 0;
    m.guardBroken[i] = POSTURE.breakTicks;
    // 공중에 뜬 몹은 띄운 궤적을 끝까지 그린 뒤 착지해서 무방비로 남는다(공중에 멈추지 않게).
    if (m.state[i] !== MobState.Airborne) {
      this.setState(i, MobState.Stunned);
      this.knockX[i] = (dirX * KNOCKBACK.normalDist) / KNOCKBACK.normalStun;
      this.knockZ[i] = (dirZ * KNOCKBACK.normalDist) / KNOCKBACK.normalStun;
    }
    const e = this.g.emit(Ev.GuardBreak);
    e.x = m.x[i]; e.z = m.z[i]; e.a = 0; e.b = i; e.c = POSTURE.breakTicks;
    return true;
  }

  private setState(i: number, state: number): void {
    if (this.dashing[i] !== 0) {
      // 돌진이 끝났거나(Attack 종료) 끊겼다(경직·처치)
      this.dashing[i] = 0;
      const m = this.g.mobs, e = this.g.emit(Ev.MobDash);
      e.x = m.x[i]; e.z = m.z[i]; e.a = i; e.b = 1; e.c = this.chargeAngle[i]; e.d = this.chargeLength[i]; e.flags = m.role[i];
    }
    this.g.releaseTelegraph(this.telegraph[i]);
    this.telegraph[i] = -1;
    this.g.mobs.state[i] = state;
    this.g.mobs.stateTick[i] = 0;
    this.forcedFall[i] = 0;
  }

  /** 세계 시간 1스텝. rate: 이번 틱 세계 배율(0..1), whole: 정수 타이머가 1틱 넘어갔는지 */
  step(rate: number, whole: boolean): void {
    const g = this.g, m = g.mobs, h = g.hero;
    if (rate <= 0) return;
    const dt = FIXED_DT * rate;
    // 흐름장은 몹이 있을 때만(영웅 칸이 바뀌었거나 0.5초가 지났을 때만 다시 만든다)
    if (this.fieldOn && m.aliveCount > 0) this.field.refresh(g.grid, g.flow.gateOpen, h.x, h.z, g.tick);
    if (whole && this.formationTick++ % MOB_FORMATION.reassignTicks === 0) this.assignSlots();
    for (let i = 0; i < m.capacity; i++) {
      const state = m.state[i];
      if (state === MobState.Inactive) continue;
      if (whole) {
        if (m.hitTick[i] < TIMER_MAX) m.hitTick[i]++;
        if (m.stateTick[i] < TIMER_MAX) m.stateTick[i]++;
        if (this.cooldown[i] > 0) this.cooldown[i]--;
        if (m.guardBroken[i] > 0) m.guardBroken[i]--;
        if (m.rooted[i] > 0) m.rooted[i]--;
        if (m.posture[i] > 0 && m.hitTick[i] >= POSTURE.decayDelay) {
          m.posture[i] = Math.max(0, m.posture[i] - POSTURE.decayPerSecond / TICK_HZ);
        }
      }
      const stats = MOB_STATS[m.kind[i]];
      if (state === MobState.Dying) {
        this.fly(i, dt, stats.radius);
        if (m.stateTick[i] >= MOBS.dyingTicks) { this.setState(i, MobState.Inactive); m.y[i] = 0; m.aliveCount--; }
        continue;
      }
      if (this.puppet[i] !== 0) { this.puppetStep(i, state); continue; }
      const dx = h.x - m.x[i], dz = h.z - m.z[i], dist = Math.hypot(dx, dz);
      // 챔피언 접두어 '신속': 이동 속도 배율(그 밖의 몹은 1)
      let spd = m.special[i] === MobSpecial.Champion ? CHAMPION_AFFIXES[m.affix[i]].speedMult : 1;
      // C2: 직군 속도 배율 · 신속 정예
      if (m.role[i] === MobRole.Thrower) spd *= ROLE.thrower.speedMult; else if (m.role[i] === MobRole.Rusher) spd *= ROLE.rusher.speedMult;
      if (this.swift(i)) spd *= AFFIX.swift.speedMult;
      // 웨이브 몹은 닫힌 방에서 영웅을 사냥한다(어그로·끈 반경 없이 추적)
      const hunting = this.waveId[i] >= 0;
      if (state === MobState.Spawning) {
        if (m.stateTick[i] >= MOBS.spawnTicks) this.setState(i, hunting || dist <= this.aggroRadius ? MobState.Chase : MobState.Idle);
        continue;
      }
      // 체공 누적이 상한을 넘으면 강제 다운(공중 정지·무한 콤보 방지, COMBAT-FEEL 2.7). 공중에서는 다운에 들어가지 않고
      // forcedFallSpeed로 떨어져 y=0이 된 틱에 다운한다(MobDown·다운 타이머는 착지 뒤, 낙하 중 다운 추격이 닿지 않는다)
      if (state === MobState.Airborne && (this.forcedFall[i] !== 0 || this.airTotal[i] + m.stateTick[i] > HIT_STATE.airCapTicks)) {
        this.forcedFall[i] = 1;
        this.knockX[i] = 0; this.knockZ[i] = 0;
        if (m.y[i] > 0) m.y[i] = Math.max(0, m.y[i] - HIT_STATE.forcedFallSpeed * dt);
        if (m.y[i] <= 0) this.enterDown(i, true);
        continue;
      }
      if (state === MobState.Stunned || state === MobState.Airborne) {
        const duration = state === MobState.Airborne ? this.airDur[i] : KNOCKBACK.normalStun;
        if (m.stateTick[i] < duration) g.moveMob(i, this.knockX[i] * rate, this.knockZ[i] * rate, stats.radius);
        if (state === MobState.Airborne) {
          const t = Math.min(1, (m.stateTick[i] + g.worldFrac) / duration);
          m.y[i] = this.airBase[i] * (1 - t) + 4 * this.airLift[i] * t * (1 - t);
        }
        if (m.stateTick[i] >= duration) {
          m.y[i] = 0;
          if (m.guardBroken[i] > 0) {
            // 무방비 중 착지: 제자리 경직 자세로 남은 무방비 시간을 보낸다(다운에는 들어가지 않는다)
            if (state === MobState.Airborne) { this.setState(i, MobState.Stunned); this.knockX[i] = 0; this.knockZ[i] = 0; this.airTotal[i] = 0; }
          } else if (state === MobState.Airborne) {
            this.airTotal[i] = 0;
            // 착지 → 다운. 엘리트는 다운도 확률로 저항해 곧바로 일어난다(기상 무적 없음)
            if (m.elite[i] !== 0 && this.resistRng.next() < HIT_STATE.eliteResist) this.setState(i, MobState.Chase);
            else this.enterDown(i, false);
          } else this.setState(i, MobState.Chase);
        }
        continue;
      }
      // 다운(행동 불가) → 기상(무적). 강제 다운으로 공중에서 시작했으면 빠르게 떨어진다
      if (state === MobState.Down) {
        if (m.y[i] > 0) m.y[i] = Math.max(0, m.y[i] - HIT_STATE.forcedFallSpeed * dt);
        if (m.stateTick[i] >= HIT_STATE.downTicks) this.enterWake(i);
        continue;
      }
      if (state === MobState.Wakeup) {
        if (m.stateTick[i] >= HIT_STATE.wakeTicks) this.setState(i, MobState.Chase);
        continue;
      }
      if (state === MobState.Windup) {
        const slot = this.telegraph[i];
        if (slot >= 0) g.telegraphs[slot].tick = Math.min(g.telegraphs[slot].total, m.stateTick[i] + g.worldFrac);
        if (m.stateTick[i] >= this.windupTicks(i)) {
          const role = m.role[i];
          if (role === MobRole.Thrower) { this.finishThrow(i, rate); continue; }
          if (role === MobRole.Summoner) { this.finishSummon(i); continue; }
          const dash = this.dasher(i);
          // 경고 데칼이 있는 공격은 데칼 모양 그대로 판정한다(발동 직전 슬롯 기하). 돌진은 지나가는 경로로 판정한다.
          const fromX = slot >= 0 ? g.telegraphs[slot].x : m.x[i], fromZ = slot >= 0 ? g.telegraphs[slot].z : m.z[i];
          const teleHit = slot >= 0 && !dash ? telegraphHit(g.telegraphs[slot], h.x, h.z) : false;
          this.telegraph[i] = -1;
          g.fireTelegraph(slot);
          this.setState(i, MobState.Attack);
          this.charged[i] = 0;
          this.cooldown[i] = Math.round((role === MobRole.Rusher ? ROLE.rusher.cooldown : stats.cooldown) * g.mobTempo.cooldownMult);
          if (dash) {
            this.dashing[i] = 1;
            const e = g.emit(Ev.MobDash); e.x = m.x[i]; e.z = m.z[i]; e.a = i; e.b = 0; e.c = this.chargeAngle[i]; e.d = this.chargeLength[i]; e.flags = role;
          }
          if (m.kind[i] === MobKind.Shaman) { if (teleHit) this.strike(i, stats.damage * g.mobDamageMult, fromX, fromZ, 2); }
          else this.attack(i, rate, slot >= 0, teleHit);
        }
        continue;
      }
      if (state === MobState.Attack) {
        // 타격 판정은 진입 틱에 한 번(돌진은 돌진하는 동안 매 틱). 남은 시간은 휘두름 자세·후딜로 제자리에 선다.
        if (m.stateTick[i] < this.attackTicksOf(i)) {
          if (this.dasher(i)) this.attack(i, rate, true, false);
        } else this.setState(i, MobState.Chase);
        continue;
      }
      if (m.kind[i] === MobKind.Shaman) { this.shaman(i, dx, dz, dist, dt); continue; }
      // 황금 고블린(스페셜): 공격하지 않고 맞으면 도망친다(game/special.ts)
      if (m.special[i] === MobSpecial.GoldGoblin) { g.special.goblinStep(i, dx, dz, dist, dt, whole); continue; }
      // Idle / Chase
      // 추적 중인 몹은 끈 반경(leashRadius)까지 놓치지 않는다. 대기 중인 몹은 어그로 반경 안에서만 깨어난다.
      if (hunting || dist <= (state === MobState.Chase ? MOBS.leashRadius : this.aggroRadius)) {
        if (dist > 1e-6) m.facing[i] = Math.atan2(dx, dz);
        if (m.role[i] === MobRole.Thrower) { this.throwerStep(i, dist, dt, spd); continue; }
        const reach = m.role[i] === MobRole.Rusher ? ROLE.rusher.trigger : this.telegraphed(i) ? stats.attackRange * TELEGRAPH_TRIGGER_RATIO : stats.attackRange + HERO.radius;
        if (dist <= reach && this.cooldown[i] === 0 && this.beginWindup(i)) continue;
        if (m.state[i] !== MobState.Chase) this.setState(i, MobState.Chase);
        // 속박(그물 덫): 제자리에서 바라보고 공격만 한다
        if (m.rooted[i] > 0) continue;
        if (!this.melee(i)) {
          if (dist > stats.radius + HERO.radius + 0.1) this.chaseMove(i, h.x, h.z, stats.speed * spd * dt, stats.radius);
        } else {
          // 진형: 공격 자리를 가진 몹은 자리로, 없는 몹은 대기 링(waitRadius)까지만 다가온다
          const F = MOB_FORMATION, s = this.slotOf[i];
          let tx: number, tz: number;
          if (s >= 0) {
            const a = (s / F.slots) * Math.PI * 2;
            // 자리 거리는 자기 사거리 안쪽(경고 원이 있는 정예는 사거리가 짧다)
            const r = Math.min(F.slotRadius, reach - 0.15);
            tx = h.x + Math.sin(a) * r; tz = h.z + Math.cos(a) * r;
          } else if (dist > 1e-6 && this.slotsHeld === 0) {
            // 맡을 자리가 하나도 없다(영웅이 벽·구멍 사이 좁은 곳): 대기 링에 서 있기만 하면 아무도 공격하지 않으니 몸이 닿을 때까지 붙는다
            const r = stats.radius + HERO.radius + 0.1;
            tx = h.x - (dx / dist) * r; tz = h.z - (dz / dist) * r;
          } else if (dist > 1e-6) {
            tx = h.x - (dx / dist) * F.waitRadius; tz = h.z - (dz / dist) * F.waitRadius;
          } else { tx = m.x[i]; tz = m.z[i]; }
          const mx = tx - m.x[i], mz = tz - m.z[i], md = Math.hypot(mx, mz);
          if (md > F.arrive) this.chaseMove(i, tx, tz, Math.min(md, stats.speed * spd * dt), stats.radius);
        }
      } else {
        if (m.state[i] !== MobState.Idle) this.setState(i, MobState.Idle);
        if (m.rooted[i] > 0) continue;
        if (whole && m.stateTick[i] % WANDER_TICKS === 0) m.facing[i] = g.rng.range(-Math.PI, Math.PI);
        const speed = stats.speed * WANDER_SPEED * dt;
        g.moveMob(i, Math.sin(m.facing[i]) * speed, Math.cos(m.facing[i]) * speed, stats.radius);
      }
    }
    this.stepShots(rate);
    this.separate();
  }

  /** 투사체 이전 위치 보관(틱 시작, 렌더 보간용). GameSim.copyPrevious가 부른다 */
  copyShotsPrevious(): void { this.shots.copyPrevious(); }

  /**
   * 투척형 AI: 사거리(range) 안에서 던지고, keepAway 안으로 영웅이 오면 물러나며, 멀면 다가온다.
   * 동시에 선딜 중인 투척형은 maxCasting마리까지(주술사 SHAMAN.maxCasting과 같은 규칙)
   */
  private throwerStep(i: number, dist: number, dt: number, spd: number): void {
    const g = this.g, m = g.mobs, h = g.hero, stats = MOB_STATS[m.kind[i]], T = ROLE.thrower;
    if (dist <= T.range && this.cooldown[i] === 0 && g.hazards.ready && g.threats.now - this.born[i] >= MOBS.spawnTicks + T.firstDelay) {
      let casting = 0;
      for (let j = 0; j < m.capacity; j++) if (m.role[j] === MobRole.Thrower && m.state[j] === MobState.Windup) casting++;
      if (casting < T.maxCasting && this.beginWindup(i)) return;
    }
    if (m.state[i] !== MobState.Chase) this.setState(i, MobState.Chase);
    if (dist < 1e-6 || m.rooted[i] > 0) return;
    const step = stats.speed * spd * dt;
    if (dist > T.range) { this.chaseMove(i, h.x, h.z, step, stats.radius); return; }
    if (dist >= T.keepAway) return;
    const dx = h.x - m.x[i], dz = h.z - m.z[i], back = -step * T.retreatMult, x = m.x[i], z = m.z[i];
    g.moveMob(i, dx / dist * back, dz / dist * back, stats.radius);
    if (Math.hypot(m.x[i] - x, m.z[i] - z) < Math.abs(back) * 0.1) {
      const side = i % 2 ? 1 : -1;
      g.moveMob(i, dz / dist * back * side, -dx / dist * back * side, stats.radius);
    }
  }

  /** 선딜 끝: 투사체를 띄운다. 경고 슬롯은 투사체가 이어받아 착탄 때까지 유지한다 */
  private finishThrow(i: number, rate: number): void {
    const g = this.g, m = g.mobs, T = ROLE.thrower, S = this.shots, slot = this.telegraph[i];
    const s = slot >= 0 ? S.alloc() : -1;
    if (s < 0) { this.setState(i, MobState.Chase); this.cooldown[i] = 30; return; }
    const t = g.telegraphs[slot];
    this.telegraph[i] = -1;
    S.alive[s] = 1; S.role[s] = MobRole.Thrower; S.owner[s] = i;
    S.startX[s] = m.x[i]; S.startZ[s] = m.z[i];
    S.x[s] = S.prevX[s] = m.x[i]; S.y[s] = S.prevY[s] = T.handY; S.z[s] = S.prevZ[s] = m.z[i];
    S.targetX[s] = t.x; S.targetZ[s] = t.z; S.progress[s] = 0; S.elapsed[s] = -rate; // 발사 틱의 stepShots가 0으로 만든다(착탄 = 발사 + flightTicks)
    S.flight[s] = T.flightTicks; S.lead[s] = this.windupTicks(i);
    S.tele[s] = slot; S.cx[s] = t.x; S.cz[s] = t.z; S.cr[s] = t.radius; // Float32에 담긴 값으로 비교한다(shotSlotValid)
    let dmg = T.damage * g.mobDamageMult;
    if (m.special[i] === MobSpecial.Champion) dmg *= CHAMPION_AFFIXES[m.affix[i]].damageMult;
    S.damage[s] = dmg; S.elite[s] = m.elite[i];
    m.facing[i] = Math.atan2(t.x - m.x[i], t.z - m.z[i]);
    const e = g.emit(Ev.MobShot); e.x = m.x[i]; e.y = T.handY; e.z = m.z[i]; e.a = s; e.b = MobRole.Thrower; e.c = m.facing[i]; e.d = T.flightTicks;
    this.setState(i, MobState.Attack);
    this.cooldown[i] = Math.round(T.cooldown * g.mobTempo.cooldownMult);
  }

  /** 경고 슬롯이 아직 이 투사체의 것인지(같은 번호가 다른 경고로 재사용되지 않았는지) */
  private shotSlotValid(s: number): boolean {
    const S = this.shots, slot = S.tele[s];
    if (slot < 0) return false;
    const t = this.g.telegraphs[slot];
    return t.active && t.shape === TeleShape.Circle && Math.fround(t.x) === S.cx[s] && Math.fround(t.z) === S.cz[s] && Math.fround(t.radius) === S.cr[s];
  }

  /** 투사체 한 스텝(세계 배율만큼 경과). 착탄 틱에 경고 슬롯 기하로 판정한다. 슬롯이 거둬졌으면(방 전환·보스 처치) 조용히 사라진다 */
  private stepShots(rate: number): void {
    const g = this.g, S = this.shots, T = ROLE.thrower, h = g.hero;
    for (let s = 0; s < MOB_SHOT_CAPACITY; s++) {
      if (S.alive[s] === 0) continue;
      if (!this.shotSlotValid(s)) { S.alive[s] = 0; S.tele[s] = -1; S.owner[s] = -1; continue; }
      const slot = S.tele[s], t = g.telegraphs[slot];
      S.elapsed[s] += rate;
      const p = Math.min(1, S.elapsed[s] / S.flight[s]);
      t.tick = Math.min(t.total, S.lead[s] + S.elapsed[s]);
      S.progress[s] = p;
      S.x[s] = S.startX[s] + (S.targetX[s] - S.startX[s]) * p;
      S.z[s] = S.startZ[s] + (S.targetZ[s] - S.startZ[s]) * p;
      S.y[s] = T.handY + (0.1 - T.handY) * p + T.arcHeight * 4 * p * (1 - p);
      if (S.elapsed[s] < S.flight[s] - 1e-6) continue;
      // 착탄: 경고 슬롯 기하 그대로 판정한다
      const hit = telegraphHit(t, h.x, h.z);
      g.fireTelegraph(slot);
      let hurt = false;
      if (hit) {
        hurt = g.combat.hurtHero(S.damage[s], S.startX[s], S.startZ[s], false, 2);
        if (hurt && S.elite[s] !== 0) g.hazards.afflict(g.map.hazardTheme);
      }
      const e = g.emit(Ev.MobShotHit); e.x = S.targetX[s]; e.z = S.targetZ[s]; e.a = s; e.b = hurt ? 1 : 0; e.c = S.cr[s];
      S.alive[s] = 0; S.progress[s] = 1; S.tele[s] = -1; S.owner[s] = -1;
    }
  }

  /** 소환형 선딜 끝: 졸개 고블린을 내보낸다(룬은 피해 없음). 소환자당·방당 상한은 canSummon이 시전 시작 때 거른다 */
  private finishSummon(i: number): void {
    const g = this.g, m = g.mobs, S = ROLE.summoner;
    let made = 0;
    for (let n = 0; n < S.minions; n++) {
      const a = (n / S.minions) * Math.PI * 2 + m.facing[i] + Math.PI * 0.5;
      if (this.spawnMinion(m.x[i] + Math.sin(a) * S.spawnRing, m.z[i] + Math.cos(a) * S.spawnRing, this.waveId[i], i, S.minionHpMult) >= 0) made++;
    }
    this.casts[i]++;
    this.cooldown[i] = Math.round(S.cooldown * g.mobTempo.cooldownMult);
    this.setState(i, MobState.Attack);
    const e = g.emit(Ev.MobSummon); e.x = m.x[i]; e.z = m.z[i]; e.a = i; e.b = 1; e.c = made; e.d = S.runeRadius;
  }

  /** 소환형이 지금 시전을 시작해도 되는지: 횟수·소환자당 졸개·방 전체 졸개 상한과 동시 시전 상한 */
  private canSummon(i: number): boolean {
    const m = this.g.mobs, S = ROLE.summoner;
    if (this.casts[i] >= S.maxCasts) return false;
    let casting = 0;
    for (let j = 0; j < m.capacity; j++) if (m.role[j] === MobRole.Summoner && m.state[j] === MobState.Windup) casting++;
    if (casting >= S.maxCasting) return false;
    return this.minionsAlive(i) + S.minions <= S.perSummoner && this.minionsAlive() + S.minions <= S.perRoom;
  }

  /**
   * 꼭두각시 몹 한 스텝(파티 판): 서버 보간 위치로 옮기고(넉백·띄움 이동은 서버 위치가 이긴다), 서버 상태로 공격 준비·휘두름 자세를 보인다.
   * 피격 경직·띄움 높이는 로컬 손맛으로 남기되 착지하면 바로 추적으로 돌아간다(다운·기상 없음 — 서버 몹은 쓰러지지 않는다)
   */
  private puppetStep(i: number, state: number): void {
    const g = this.g, m = g.mobs, p = g.party, stats = MOB_STATS[m.kind[i]];
    m.x[i] = p.tx[i]; m.z[i] = p.tz[i];
    if (state === MobState.Spawning) { if (m.stateTick[i] >= MOBS.spawnTicks) this.setState(i, MobState.Chase); return; }
    if (state === MobState.Airborne) {
      const duration = Math.max(1, this.airDur[i]);
      const t = Math.min(1, (m.stateTick[i] + g.worldFrac) / duration);
      m.y[i] = this.airBase[i] * (1 - t) + 4 * this.airLift[i] * t * (1 - t);
      if (m.stateTick[i] >= duration || this.airTotal[i] + m.stateTick[i] > HIT_STATE.airCapTicks) { m.y[i] = 0; this.airTotal[i] = 0; this.setState(i, MobState.Chase); }
      return;
    }
    if (state === MobState.Stunned || state === MobState.Down || state === MobState.Wakeup) {
      m.y[i] = 0;
      if (m.stateTick[i] >= KNOCKBACK.normalStun && m.guardBroken[i] === 0) this.setState(i, MobState.Chase);
      return;
    }
    m.facing[i] = p.tf[i];
    const sst = p.sst[i];
    if (sst === PMobSt.Windup) {
      if (state !== MobState.Windup) { this.setState(i, MobState.Windup); m.windupTicks[i] = stats.windup; }
      return;
    }
    // 서버가 판정을 끝냈다(회복·추적): 휘두름 자세를 attackTicks만큼 보인다
    if (state === MobState.Windup) { this.setState(i, MobState.Attack); return; }
    if (state === MobState.Attack) { if (m.stateTick[i] >= stats.attackTicks) this.setState(i, MobState.Chase); return; }
    if (state !== MobState.Chase) this.setState(i, MobState.Chase);
  }

  private shaman(i: number, dx: number, dz: number, dist: number, dt: number): void {
    const g = this.g, m = g.mobs, stats = MOB_STATS[MobKind.Shaman];
    if (dist > 1e-6) m.facing[i] = Math.atan2(dx, dz);
    if (m.role[i] === MobRole.Summoner) {
      // 소환형: 포격 대신 소환 룬(피해 없음). 이동(거리 유지)은 주술사와 같다
      if (dist <= stats.attackRange && this.cooldown[i] === 0 && g.hazards.ready && g.threats.now - this.born[i] >= MOBS.spawnTicks + ROLE.summoner.firstDelay
        && this.canSummon(i) && this.beginWindup(i)) return;
    } else if (dist <= stats.attackRange && this.cooldown[i] === 0 && g.hazards.ready && g.threats.now - this.born[i] >= MOBS.spawnTicks + SHAMAN.firstCastDelay) {
      let casting = 0;
      for (let j = 0; j < m.capacity; j++) if (m.kind[j] === MobKind.Shaman && m.role[j] !== MobRole.Summoner && m.state[j] === MobState.Windup) casting++;
      if (casting < SHAMAN.maxCasting && this.beginWindup(i)) return;
    }
    if (m.state[i] !== MobState.Chase) this.setState(i, MobState.Chase);
    if (dist < 1e-6 || m.rooted[i] > 0) return;
    const speed = stats.speed * dt * (dist < SHAMAN.keepAway ? -SHAMAN.retreatMult : dist > stats.attackRange ? 1 : 0);
    const x = m.x[i], z = m.z[i];
    g.moveMob(i, dx / dist * speed, dz / dist * speed, stats.radius);
    if (speed < 0 && Math.hypot(m.x[i] - x, m.z[i] - z) < Math.abs(speed) * 0.1) {
      const side = i % 2 ? 1 : -1;
      g.moveMob(i, dz / dist * speed * side, -dx / dist * speed * side, stats.radius);
      if (m.x[i] === x && m.z[i] === z) g.moveMob(i, -dz / dist * speed * side, dx / dist * speed * side, stats.radius);
    }
  }

  /** 선딜 시작. 멧돼지·엘리트·주술사·투척형·돌진형이 텔레그래프 슬롯을 쓰고, 슬롯이 없으면 공격을 미룬다(소환형은 슬롯 없이 룬만) */
  private beginWindup(i: number): boolean {
    const g = this.g, m = g.mobs, stats = MOB_STATS[m.kind[i]], role = m.role[i];
    const summoner = role === MobRole.Summoner, thrower = role === MobRole.Thrower, rusher = role === MobRole.Rusher;
    const shaman = m.kind[i] === MobKind.Shaman && !summoner;
    const dash = m.kind[i] === MobKind.Boar || rusher;
    const needsSlot = shaman || dash || thrower || m.elite[i] !== 0;
    // 공격권: 일반 근접 몹은 동시에 attackTokens마리까지만 선딜·공격에 들어간다(몰려 있어도 공격이 읽히게)
    if (!needsSlot && !summoner && this.attackersNow() >= g.mobTempo.attackTokens) return false;
    if (thrower && !this.shots.hasFree()) return false;
    // 투척형은 착탄까지가 반응 시간이다(위험 예약표도 착탄 순간으로 잡는다)
    const lead = this.windupTicks(i), total = thrower ? lead + ROLE.thrower.flightTicks : lead;
    let slot = -1;
    if (needsSlot) {
      slot = g.allocateTelegraph(shaman ? TeleSource.Shaman : TeleSource.Mob, i);
      if (slot < 0) return false;
      if (g.map.threatClock && !g.threats.reserve(slot, g.threats.now + total)) { g.releaseTelegraph(slot); return false; }
    }
    this.setState(i, MobState.Windup);
    this.telegraph[i] = slot;
    m.windupTicks[i] = this.windupTicks(i); // 렌더가 같은 길이로 선딜 자세를 재생한다(공격 발동 틱과 일치)
    const reachLen = rusher ? ROLE.rusher.dashLength : stats.attackRange;
    this.chargeLength[i] = reachLen;
    if (dash) {
      for (let d = 0.25; d <= reachLen; d += 0.25) {
        if (!g.walkable(m.x[i] + Math.sin(m.facing[i]) * d, m.z[i] + Math.cos(m.facing[i]) * d, stats.radius, -1, true)) { this.chargeLength[i] = d - 0.25; break; }
      }
    }
    this.chargeX[i] = m.x[i]; this.chargeZ[i] = m.z[i]; this.chargeAngle[i] = m.facing[i];
    if (slot >= 0) {
      const t = g.telegraphs[slot];
      t.shape = dash ? TeleShape.Line : m.kind[i] === MobKind.ShieldGoblin ? TeleShape.Cone : TeleShape.Circle;
      t.x = m.x[i]; t.z = m.z[i]; t.angle = m.facing[i]; t.tick = 0; t.total = total;
      t.radius = dash ? this.chargeLength[i] : stats.attackRange;
      if (shaman) {
        t.x = g.hero.x; t.z = g.hero.z; t.radius = SHAMAN.mortarRadius;
        const e = g.emit(Ev.ShamanCast); e.x = t.x; e.z = t.z; e.a = i; e.b = t.radius;
      }
      if (thrower) { t.x = g.hero.x; t.z = g.hero.z; t.radius = ROLE.thrower.radius; }
      t.width = t.shape === TeleShape.Cone ? SHIELD_ARC : t.shape === TeleShape.Line ? (rusher ? ROLE.rusher.width : stats.radius * 2) : 0;
    }
    if (summoner) {
      const e = g.emit(Ev.MobSummon); e.x = m.x[i]; e.z = m.z[i]; e.a = i; e.b = 0; e.c = ROLE.summoner.minions; e.d = ROLE.summoner.runeRadius;
    }
    return true;
  }

  /**
   * 공격 판정. telegraphed: 경고 데칼이 있던 공격(영웅 중심점 기준), teleHit: 발동 순간 데칼 판정 결과.
   * 일반 몹은 데칼이 없으므로 영웅 몸 반경까지 넉넉히 맞는다.
   */
  private attack(i: number, rate: number, telegraphed: boolean, teleHit: boolean): void {
    const g = this.g, m = g.mobs, h = g.hero, stats = MOB_STATS[m.kind[i]];
    if (this.dasher(i)) {
      // 돌진: 세계 틱당 사거리/돌진 틱만큼 이동. 이번 틱 지나간 경로이면서 그려진 경고선 안이어야 맞고, 한 번만 맞힌다.
      const rush = m.role[i] === MobRole.Rusher, R = ROLE.rusher;
      const oldX = m.x[i], oldZ = m.z[i], step = ((rush ? R.dashLength : stats.attackRange) / (rush ? R.dashTicks : CHARGE_TICKS)) * rate;
      // 그물 덫 속박 중이면 돌진 이동도 멈춘다(속박 = 이동만 정지, 제자리 판정은 그대로)
      if (m.rooted[i] === 0) g.moveMob(i, Math.sin(m.facing[i]) * step, Math.cos(m.facing[i]) * step, stats.radius);
      const width = rush ? R.width : stats.radius * 2;
      if (!this.charged[i] && segmentHit(oldX, oldZ, m.x[i], m.z[i], width, h.x, h.z, 0)
        && lineRectHit(this.chargeX[i], this.chargeZ[i], this.chargeAngle[i], this.chargeLength[i], width, h.x, h.z)) {
        this.charged[i] = 1;
        this.strike(i, (rush ? R.damage : stats.damage) * g.mobDamageMult, oldX, oldZ, 2);
      }
      return;
    }
    // 휘두름 판정 순간(맞든 빗나가든): 렌더·VFX가 궤적을 이 틱에 맞춘다
    const sw = g.emit(Ev.MobSwing); sw.x = m.x[i]; sw.z = m.z[i]; sw.a = i; sw.b = m.kind[i]; sw.c = m.facing[i]; sw.d = m.elite[i]; sw.flags = m.role[i];
    const hit = telegraphed ? teleHit : m.kind[i] === MobKind.ShieldGoblin
      ? coneHit(m.x[i], m.z[i], m.facing[i], stats.attackRange, SHIELD_ARC, h.x, h.z, HERO.radius)
      : circleHit(m.x[i], m.z[i], h.x, h.z, stats.attackRange, HERO.radius);
    if (hit) this.strike(i, stats.damage * g.mobDamageMult, m.x[i], m.z[i], telegraphed ? 2 : 0);
  }

  /** 영웅 타격. 정예가 맞히면 지역 테마 상태 이상(서리 둔화·중독, D3a)을 건다(용암 테마는 효과 없음) */
  private strike(i: number, damage: number, fromX: number, fromZ: number, source: number): void {
    const g = this.g;
    // 챔피언 접두어 '강철': 영웅에게 주는 피해 배율
    if (g.mobs.special[i] === MobSpecial.Champion) damage *= CHAMPION_AFFIXES[g.mobs.affix[i]].damageMult;
    if (g.combat.hurtHero(damage, fromX, fromZ, false, source) && g.mobs.elite[i] !== 0) g.hazards.afflict(g.map.hazardTheme);
  }

  private hash(x: number, z: number): number { return (Math.imul(x, 73856093) ^ Math.imul(z, 19349663)) & (HASH_BUCKETS - 1); }

  /** 지금 선딜·공격 중인 일반 근접 몹 수(멧돼지·엘리트 제외) */
  private attackersNow(): number {
    const m = this.g.mobs;
    let n = 0;
    for (let i = 0; i < m.capacity; i++) {
      const st = m.state[i];
      if ((st === MobState.Windup || st === MobState.Attack) && m.kind[i] !== MobKind.Boar && m.kind[i] !== MobKind.Shaman && m.elite[i] === 0 && m.role[i] === MobRole.Base) n++;
    }
    return n;
  }

  /** 몹끼리 겹치지 않게 미는 위치 제약(재사용 공간 해시, 해시 충돌은 셀 좌표로 거른다) */
  /**
   * 추적 이동 한 스텝: (tx,tz) 쪽으로 최대 step(m). 영웅에게서 장 걸음 CHASE_FAR칸보다 멀거나 직전에 벽에 막혔으면
   * 흐름장을 내려간 점을 대신 겨눈다(기둥·벽·구멍 뒤에서 서지 않고 돌아온다). 요청한 거리의 1/4도 못 가면 우회를 DETOUR_TICKS 유지한다.
   */
  private chaseMove(i: number, tx: number, tz: number, step: number, radius: number): void {
    const g = this.g, m = g.mobs, x0 = m.x[i], z0 = m.z[i];
    if (this.detour[i] > 0) this.detour[i]--;
    if (this.fieldOn && (this.detour[i] > 0 || this.field.stepsAt(x0, z0) > CHASE_FAR) && this.field.steer(x0, z0, g.hero.x, g.hero.z, this.aim)) { tx = this.aim.x; tz = this.aim.z; }
    const mx = tx - x0, mz = tz - z0, md = Math.hypot(mx, mz);
    if (md < 1e-6 || step <= 0) return;
    const s = Math.min(step, md);
    g.moveMob(i, (mx / md) * s, (mz / md) * s, radius);
    if (s > 0.01 && Math.hypot(m.x[i] - x0, m.z[i] - z0) < s * 0.25) this.detour[i] = DETOUR_TICKS;
  }

  private separate(): void {
    const g = this.g, m = g.mobs;
    for (let pass = 0; pass < 2; pass++) {
      this.heads.fill(-1);
      for (let i = 0; i < m.capacity; i++) {
        if (m.state[i] === MobState.Inactive || m.state[i] === MobState.Dying) continue;
        const x = (this.cellX[i] = Math.floor(m.x[i] / HASH_CELL));
        const z = (this.cellZ[i] = Math.floor(m.z[i] / HASH_CELL));
        const bucket = this.hash(x, z);
        this.next[i] = this.heads[bucket];
        this.heads[bucket] = i;
      }
      for (let i = 0; i < m.capacity; i++) {
        if (m.state[i] === MobState.Inactive || m.state[i] === MobState.Dying || this.puppet[i] !== 0) continue;
        for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) {
          const cx = this.cellX[i] + dx, cz = this.cellZ[i] + dz;
          for (let j = this.heads[this.hash(cx, cz)]; j >= 0; j = this.next[j]) {
            if (j <= i || this.cellX[j] !== cx || this.cellZ[j] !== cz || this.puppet[j] !== 0) continue;
            let px = m.x[j] - m.x[i], pz = m.z[j] - m.z[i], distance = Math.hypot(px, pz);
            const radiusI = this.radius(i), radiusJ = this.radius(j);
            // 서로 밀어내는 간격은 겉모습 폭에 맞춘 개인 공간(몸 반경 × personalSpace). 벽 충돌은 원래 반경 그대로
            const overlap = (radiusI + radiusJ) * MOB_FORMATION.personalSpace - distance;
            if (overlap <= 0) continue;
            if (distance < 1e-5) { px = (i + j) % 2 ? 1 : -1; pz = 0; distance = 1; }
            const shift = overlap * 0.5 + 0.001, sx = (px / distance) * shift, sz = (pz / distance) * shift;
            g.moveMob(i, -sx, -sz, radiusI);
            g.moveMob(j, sx, sz, radiusJ);
          }
        }
      }
    }
  }
}
